// UploadContent.jsx
import React, { useState } from 'react';
import { 
  Upload, 
  Book, 
  BookOpen, 
  FileAudio, 
  Video, 
  Film,
  X,
  Image,
  Plus,
  AlertCircle
} from 'lucide-react';
import styles from './UploadContent.module.css';

const UploadContent = () => {
  const [contentType, setContentType] = useState('');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: '',
    category: '',
    duration: '',
    releaseDate: ''
  });
  const [dragActive, setDragActive] = useState(false);

  const contentTypes = [
    { id: 'ebook', label: 'E-Book', icon: Book, accepts: '.pdf,.epub,.mobi' },
    { id: 'book', label: 'Book', icon: BookOpen, accepts: '.pdf,.epub,.mobi' },
    { id: 'audio', label: 'Audio', icon: FileAudio, accepts: '.mp3,.wav,.m4a' },
    { id: 'video', label: 'Video', icon: Video, accepts: '.mp4,.mov,.avi' },
    { id: 'shortvideo', label: 'Short Video', icon: Film, accepts: '.mp4,.mov,.avi' }
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFileType(droppedFile)) {
      setFile(droppedFile);
    }
  };

  const validateFileType = (file) => {
    const selectedType = contentTypes.find(type => type.id === contentType);
    if (!selectedType) return false;
    
    const acceptedTypes = selectedType.accepts.split(',');
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return acceptedTypes.includes(fileExtension);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && validateFileType(selectedFile)) {
      setFile(selectedFile);
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setThumbnail(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log({
      contentType,
      file,
      thumbnail,
      ...formData
    });
  };

  const renderFields = () => {
    const commonFields = (
      <>
        <div className={styles.formGroup}>
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Enter title"
          />
        </div>
        <div className={styles.formGroup}>
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter description"
            rows="4"
          />
        </div>
        <div className={styles.formGroup}>
          <label>Category</label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            placeholder="Enter category"
          />
        </div>
      </>
    );

    const additionalFields = {
      ebook: (
        <>
          <div className={styles.formGroup}>
            <label>Author</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              placeholder="Enter author name"
            />
          </div>
        </>
      ),
      audio: (
        <>
          <div className={styles.formGroup}>
            <label>Duration</label>
            <input
              type="text"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="Enter duration (e.g., 45:30)"
            />
          </div>
        </>
      ),
      video: (
        <>
          <div className={styles.formGroup}>
            <label>Duration</label>
            <input
              type="text"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="Enter duration (e.g., 1:30:00)"
            />
          </div>
        </>
      ),
      shortvideo: (
        <>
          <div className={styles.formGroup}>
            <label>Duration</label>
            <input
              type="text"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="Enter duration (max 60 seconds)"
            />
          </div>
        </>
      )
    };

    return (
      <>
        {commonFields}
        {additionalFields[contentType]}
      </>
    );
  };

  return (
    <div className={styles.container}>
    <div className={styles.uploadContainer}>
      <h2 className={styles.title}>Upload New Content</h2>
      
      {/* Content Type Selection */}
      <div className={styles.contentTypeGrid}>
        {contentTypes.map((type) => (
          <button
            key={type.id}
            className={`${styles.contentTypeBtn} ${contentType === type.id ? styles.active : ''}`}
            onClick={() => setContentType(type.id)}
          >
            <type.icon size={24} />
            <span>{type.label}</span>
          </button>
        ))}
      </div>

      {contentType && (
        <form onSubmit={handleSubmit} className={styles.uploadForm}>
          {/* File Upload Area */}
          <div 
            className={`${styles.dropzone} ${dragActive ? styles.dragActive : ''} ${file ? styles.hasFile : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept={contentTypes.find(type => type.id === contentType)?.accepts}
              onChange={handleFileChange}
              className={styles.fileInput}
            />
            
            {file ? (
              <div className={styles.fileInfo}>
                <div className={styles.filePreview}>
                  <div className={styles.fileName}>
                    {file.name}
                    <button 
                      type="button" 
                      className={styles.removeBtn}
                      onClick={() => setFile(null)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className={styles.fileSize}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.uploadPrompt}>
                <Upload size={32} />
                <p>Drag & drop your file here or click to browse</p>
                <span className={styles.acceptedFiles}>
                  Accepted files: {contentTypes.find(type => type.id === contentType)?.accepts}
                </span>
              </div>
            )}
          </div>

          {/* Thumbnail Upload */}
          {(contentType === 'video' || contentType === 'shortvideo') && (
            <div className={styles.thumbnailUpload}>
              <label>Thumbnail Image</label>
              <div className={styles.thumbnailArea}>
                {thumbnail ? (
                  <div className={styles.thumbnailPreview}>
                    <Image size={24} />
                    <span>{thumbnail.name}</span>
                    <button 
                      type="button"
                      className={styles.removeThumbnail}
                      onClick={() => setThumbnail(null)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.thumbnailPrompt}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className={styles.thumbnailInput}
                    />
                    <Plus size={24} />
                    <span>Add Thumbnail</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Details Fields */}
          {renderFields()}

          {/* Submit Button */}
          <button type="submit" className={styles.submitBtn}>
            <Upload size={20} />
            Upload Content
          </button>
        </form>
      )}
    </div>
    </div>
  );
};

export default UploadContent;